<template>
	<div class="library library-row">
		<v-container
			fluid
			:full="full"
			class="pa-4"
			style="height: 100%; margin: 0; width: 100%; min-height: 95vh"
		>
			<!-- HEADER -->

			<div slot="title">
				<h1
					:style="{
						position: 'relative'
					}"
					v-if="category.name === 'all'"
					style="margin-left: 12px !important"
				>
					Marketing Library
				</h1>

				<!-- BANNERS -->

				<div v-if="category.name !== 'all'">
					<banner>
						<img
							slot="image"
							:src="category.image_url"
							:alt="`${category.name} category`"
							:style="{
								height: '100%',
								width: '100%',
								objectFit: 'cover',
								zIndex: 1
							}"
						/>
						<div
							slot="back-button"
							class="banner-back"
							v-on:click="setCategory({ name: 'all' })"
						>
							<i class="fas fa-chevron-left"></i>
							<p class="text">
								Back
							</p>
						</div>
						<div slot="title" class="banner-title">
							<p class="text">{{ category.name }}</p>
						</div>
						<div slot="sub-title" class="banner-sub-title">
							<p class="text">
								{{ category.banner_text }}
							</p>
						</div>
						<div slot="download-button" class="banner-dl-button">
							<Roundup-Button
								color="primary"
								:loading="marketingLoading"
								@click="downloadZip(category.name)"
								:disabled="marketingSize[category.name] === 0"
								>{{ downloadButtonText }}</Roundup-Button
							>
						</div>
					</banner>
				</div>
			</div>
			<div slot="body" class="col">
				<div class="library-row" v-if="category.name === 'all'">
					<p>
						Find dozens of templates and examples to help you
						promote your RoundUp App program.
					</p>
				</div>

				<!-- NAV TABS -->

				<div
					class="library-row"
					style="margin-bottom: 0px"
					v-if="category.name === 'all'"
				>
					<div class="tab" @click="setResource('donor')">
						<p :class="[resource === 'donor' ? 'selected' : '']">
							Donor Resources
						</p>
					</div>
					<div
						class="tab"
						style="margin-left: 20px"
						@click="setResource('support')"
					>
						<p :class="[resource === 'support' ? 'selected' : '']">
							Support Resources
						</p>
					</div>
				</div>

				<div class="library-row" style="margin-top: 0px">
					<div
						v-for="(value, name) of marketingCategories"
						:key="name"
					>
						<div
							v-if="
								resource === value.resource &&
									category.name === 'all'
							"
						>
							<div @click="setCategory(value)">
								<file-tile type="nav">
									<img
										slot="image"
										:src="value.image_url"
										:alt="`${value.name} category`"
										:style="{
											height: '100%',
											width: '100%',
											objectFit: 'cover',
											borderRadius: '10px'
										}"
									/>
									<p slot="title" class="nav-title">
										{{ value.name }}
									</p>
								</file-tile>
							</div>
						</div>
					</div>
				</div>

				<!-- DONOR IMAGES -->

				<div
					v-if="!marketingLoading && resource === 'donor'"
					style="width:100%"
				>
					<div
						class="library-row"
						style="width:100%"
						v-for="(value, name) in marketingTemplates"
						:key="name"
					>
						<div v-show="hasCategory(value)">
							<carousel :id="formatTypeWithHyphens(name)">
								<p
									slot="title"
									class="carousel-text"
									style="margin-bottom: 20px !important;"
								>
									{{ name }}
								</p>
								<div
									slot="slides"
									v-for="slide in filterTemplates(value)"
									class="slide"
									:key="slide.id"
								>
									<div
										@click="openModal(slide)"
										style="cursor: pointer; padding-bottom: 20px; position: relative;"
									>
										<img
											:src="slide.url"
											:alt="slide.type"
											:class="[
												formatTypeWithHyphens(name),
												'slide-img'
											]"
										/>
										<span
											class="canva-tag"
											v-if="slide.canva_url"
										>
											<span class="canva-btn-i"></span>
										</span>
									</div>
								</div>
							</carousel>
						</div>
					</div>
				</div>

				<!-- SUPPORT PDFS -->

				<div
					v-if="!marketingLoading && resource === 'support'"
					style="justify-content: space-between; width:100%;"
				>
					<div
						class="library-row"
						style="justify-content: space-between; width:100%; overflow-y: hidden"
						v-for="(value, name) in marketingFiles"
						:key="name"
					>
						<div v-show="hasCategory(value)">
							<carousel>
								<p
									slot="title"
									class="carousel-text"
									style="margin-bottom: 20px !important;"
								>
									{{ setCategoryName(name) }}
								</p>
								<div
									slot="slides"
									v-for="slide in filterTemplates(
										marketingFiles[name]
									)"
									class="slide"
									:key="slide.id"
								>
									<pdf
										:fileUrl="slide.url"
										:height="240"
										:width="240"
										@click="trackPDFDownload(slide)"
									/>
									<p
										class="slide-title"
										style="width: 240px; margin-bottom: 20px !important; margin-top: 5px !important; font-weight: 500; font-size: 0.875rem"
									>
										{{ slide.title }}
									</p>
								</div>
							</carousel>
						</div>
					</div>
				</div>
			</div>

			<!-- MODAL -->

			<v-dialog v-model="modal.show" max-width="960px">
				<detail-view
					:slide="modal.data"
					:institution="institution"
					:url="deeplink"
					@close="modal.show = false"
				>
				</detail-view>
			</v-dialog>
		</v-container>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import UiButton from '../../ui/Button'
import Container from '../../ui/Container'
import UiLoader from '../../ui/Loader'
import FileTile from '../../ui/FileTile'
import Modal from '../../ui/Modal'
import PDF from './PDF'
import Banner from './Banner'
import Carousel from './Carousel'
import DetailView from './DetailView'
import { formatStrWithHyphens } from '../../../utilities'

export default {
	name: 'library',
	components: {
		UiButton,
		UiLoader,
		Container,
		FileTile,
		Banner,
		Carousel,
		Modal,
		DetailView,
		pdf: PDF
	},
	data() {
		return {
			modal: { show: false, data: {} },
			resource: 'donor',
			category: { name: 'all' },
			full: false
		}
	},
	computed: {
		...mapGetters([
			'marketingFiles',
			'marketingCategories',
			'marketingTemplates',
			'marketingLoading',
			'marketingZip',
			'marketingSize',
			'institution'
		]),
		isSpanish() {
			return this.category.name === 'Soporte En Español'
		},
		deeplink() {
			return 'https://roundup.app/p/' + this.institution.username
		},
		downloadButtonText() {
			if (this.isSpanish) {
				return `Descarge Todo Los Materiales En Español `
			} else {
				return `Download All ${this.category.name} Resources`
			}
		}
	},
	watch: {
		category: function() {
			if (this.category.name === 'all') {
				this.full = false
			} else {
				this.full = true
			}
		}
	},
	methods: {
		...mapActions(['getLibrary', 'getLibraryZip']),
		async downloadZip(category) {
			if (window.analytics) {
				window.analytics.track('marketing_library_download', {
					category,
					zip: true
				})
			}
			await this.getLibraryZip(category)
			window.open(this.marketingZip, '_blank')
		},
		setCategoryName(name) {
			if (this.isSpanish) {
				switch (name) {
					case 'How To Guides':
						return 'Guías'
					case 'Promotional Handouts':
						return 'Materiales Promocionales'
					default:
						return name
				}
			} else {
				return name
			}
		},
		setResource(r) {
			this.resource = r
			this.category.name = 'all'
		},
		setCategory(c) {
			this.category = c
		},
		formatTypeWithHyphens(t) {
			return formatStrWithHyphens(t)
		},
		filterTemplates(templates) {
			if (this.category.name === 'all') {
				return templates.filter(
					t => t.category.name !== 'Soporte En Español'
				)
			}
			let t = templates.filter(
				t => t.category.name === this.category.name
			)

			return t
		},
		hasCategory(templates) {
			if (this.category.name === 'all') return true
			if (templates.length === 0) return false
			for (var template of templates) {
				if (template.category.name === this.category.name) {
					return true
				}
			}
			return false
		},
		openModal(slide) {
			this.modal.data = slide
			this.modal.show = true
			if (window.analytics) {
				window.analytics.track('marketing_library_details', {
					title: slide.title,
					type: slide.type,
					url: url.replace(
						'https://admin-marketing-library.s3.amazonaws.com/',
						''
					),
					image: true
				})
			}
		}
	},
	mounted() {
		let hsScript = document.createElement('script')
		hsScript.setAttribute(
			'src',
			'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js'
		)
		document.head.appendChild(hsScript)

		this.getLibrary()
	}
}
</script>

<style lang="scss">
@import '../../../styles/variables';
.library {
	width: 100%;
	.library-row {
		display: flex;
		flex-direction: row;
		max-width: 100%;
		flex-wrap: wrap;
		justify-content: left;
		margin: 12px 0px;
		margin-top: 0px !important;
	}
	.col {
		display: flex;
		flex-direction: column;
		max-width: 100%;
		flex-wrap: wrap;
		align-items: flex-start;
	}
	.banner-back {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		width: 61px;
		height: 20px;
		cursor: pointer;
		&:hover {
			transform: translateY(-2px);
		}
	}
	.banner-title {
		.text {
			font-size: 48px;
			font-weight: $font-medium !important;
		}
	}
	.fa-chevron-left {
		color: #fff;
	}
	.tab {
		margin-right: 20px;
		cursor: pointer;
		:hover {
			color: $roundup-darker-blue;
		}
		p {
			font-weight: 600;
			color: $roundup-charcoal !important;
			font-size: 16px;
		}
		.selected {
			border-bottom: solid $roundup-navy 4px;
			color: $roundup-navy;
		}
	}
	.slide {
		height: 100%;
		transition: transform 0.3s ease-in-out;
		margin-left: 10px;
		margin-right: 10px;
		border-radius: 10px;
		.slide-img {
			border-radius: 10px;
		}
		.slide-title {
			white-space: nowrap;
			text-overflow: ellipsis;
			overflow: hidden;
		}
		.instagram-posts {
			max-width: 218px;
		}
		.facebook-posts {
			max-width: 436px;
		}
		.emails {
			max-width: 654px;
			height: 160px;
		}
		.text-messages {
			height: 275px;
		}
	}
	.nav-title {
		text-align: center;
		font-weight: 600;
	}
}
.library p,
.library .carousel-text {
	color: $roundup-dark-grey;
	margin: 0;
}
.library .banner-title .text,
.library .banner-back .text {
	font-weight: bold;
}
.library .text,
.library .nav-title {
	color: #fff;
}
.library .slide .facebook-posts,
.library .slide .instagram-posts,
.library .slide .twitter-posts {
	height: 218px;
}
.library .slide .how-to-guides,
.library .slide .promotional-handouts {
	height: 240px;
	width: 170px;
}
.library .nav,
.library .file {
	border-radius: 10px;
}

.canva-tag {
	position: absolute;
	top: -5px;
	right: -5px;
	height: 40px;
	width: 40px;
	background-color: white;
	border-radius: 20px;
	justify-content: center;
	align-items: center;
	display: flex;

	.canva-btn-i {
		filter: drop-shadow(3px 3px 4px rgba(0, 0, 0, 0.1));
		display: block;
		height: 25px;
		width: 25px;
		background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3ccircle cx='12' cy='12' r='12' fill='%235A32FA'/%3e%3ccircle cx='12' cy='12' r='12' fill='url(%23paint0_radial)'/%3e%3ccircle cx='12' cy='12' r='12' fill='url(%23paint1_radial)'/%3e%3ccircle cx='12' cy='12' r='12' fill='url(%23paint2_radial)'/%3e%3ccircle cx='12' cy='12' r='12' fill='url(%23paint3_radial)'/%3e%3cpath d='M17.1807 14.4617C17.0816 14.4617 16.9945 14.5452 16.9038 14.7279C15.8797 16.8047 14.1108 18.2741 12.0637 18.2741C9.69682 18.2741 8.23108 16.1375 8.23108 13.1857C8.23108 8.18574 11.0169 5.29483 13.4638 5.29483C14.6073 5.29483 15.3056 6.01338 15.3056 7.15685C15.3056 8.51398 14.5345 9.23252 14.5345 9.71116C14.5345 9.92601 14.6682 10.0561 14.9332 10.0561C15.9979 10.0561 17.2476 8.83267 17.2476 7.10433C17.2476 5.42851 15.789 4.19672 13.3421 4.19672C9.29816 4.19672 5.70422 7.94582 5.70422 13.1332C5.70422 17.1485 7.99713 19.8019 11.535 19.8019C15.29 19.8019 17.4612 16.0659 17.4612 14.8532C17.4612 14.5846 17.3239 14.4617 17.1807 14.4617Z' fill='white'/%3e%3cdefs%3e%3cradialGradient id='paint0_radial' cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='translate(4.63579 21.2716) rotate(-49.4156) scale(18.5619)'%3e%3cstop stop-color='%235A32FA'/%3e%3cstop offset='1' stop-color='%235A32FA' stop-opacity='0'/%3e%3c/radialGradient%3e%3cradialGradient id='paint1_radial' cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='translate(6.35368 2.72842) rotate(54.7035) scale(20.9321)'%3e%3cstop stop-color='%2300C4CC'/%3e%3cstop offset='1' stop-color='%2300C4CC' stop-opacity='0'/%3e%3c/radialGradient%3e%3cradialGradient id='paint2_radial' cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='translate(4.63579 21.2716) rotate(-45.1954) scale(18.3372 8.43355)'%3e%3cstop stop-color='%235A32FA'/%3e%3cstop offset='1' stop-color='%235A32FA' stop-opacity='0'/%3e%3c/radialGradient%3e%3cradialGradient id='paint3_radial' cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='translate(7 3) rotate(62.4472) scale(25.9422 43.459)'%3e%3cstop stop-color='%2300C4CC' stop-opacity='0.725916'/%3e%3cstop offset='0.0001' stop-color='%2300C4CC'/%3e%3cstop offset='1' stop-color='%2300C4CC' stop-opacity='0'/%3e%3c/radialGradient%3e%3c/defs%3e%3c/svg%3e ");
	}
}
</style>
